import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import ProfileChart from "@/components/wifi/ProfileChart";

const inp = "bg-slate-800 border border-slate-700 rounded px-2 py-1 text-[11px] outline-none focus:ring-1 focus:ring-cyan-500";
const LVL = { ok: "text-emerald-300 border-emerald-700 bg-emerald-950/30", warn: "text-amber-300 border-amber-700 bg-amber-950/30", error: "text-red-300 border-red-700 bg-red-950/30" };
const errMsg = (e) => { const d = e.response?.data?.detail; return d?.issues ? `${d.msg}: ${d.issues.join("; ")}` : formatApiError(d); };

function Box({ title, children, tid }) {
  return <div className="m-3 p-3 rounded border border-slate-700 bg-slate-800/40 text-[11px]" data-testid={tid}><div className="font-semibold text-slate-200 mb-2">{title}</div>{children}</div>;
}

function Result({ p }) {
  const b = p.budget;
  const m = [["Odległość", `${(p.distance_m / 1000).toFixed(2)} km`], ["Najgorszy prześwit", `${p.worst_ratio_pct}% F1`], ["F1 w połowie", `${p.fresnel_r1_mid_m} m`],
    ["Strata dyfrakcyjna", `${p.diffraction_db} dB`], ["Tilt A / B", `${p.tilt_a_deg}° / ${p.tilt_b_deg}°`], ["Teren A / B", `${p.a.ground_m} / ${p.b.ground_m} m`]];
  if (b) m.push(["RSSI z terenem", `${b.rssi_terrain_dbm} dBm`], ["SNR z terenem", `${b.snr_terrain_db} dB`], ["Przepustowość (bez terenu)", `~${b.throughput_mbps} Mb/s`]);
  return (
    <div className="mt-3 space-y-2" data-testid="wifi-los-result">
      <div className={`border rounded px-2 py-1.5 font-semibold ${LVL[p.level]}`} data-testid="wifi-los-verdict">{p.label}</div>
      <ProfileChart p={p} />
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">{m.map(([l, v]) => <div key={l} className="bg-slate-900/60 rounded p-1.5"><div className="text-[9px] text-slate-500 uppercase">{l}</div><div className="font-mono">{v}</div></div>)}</div>
      {p.raise_both_m > 0 && <div className="text-amber-300" data-testid="wifi-los-raise">Aby uzyskać 60% strefy Fresnela, podnieś obie anteny o ok. {p.raise_both_m} m (lub jedną odpowiednio wyżej).</div>}
      {p.worst?.d != null && <div className="text-slate-400">Krytyczny punkt: {(p.worst.d / 1000).toFixed(2)} km od A ({p.worst.lat?.toFixed(5)}, {p.worst.lng?.toFixed(5)}), teren {p.worst.terrain} m, linia LOS {p.worst.los} m.</div>}
      <div className="text-[10px] text-slate-500">Źródło wysokości: {p.source}. Model bez zabudowy — wysokość drzew/budynków wpisz w polu „Przeszkody”. {p.saved ? "Wynik zapisano w obiekcie." : ""}</div>
    </div>
  );
}

function LosAnalyzer({ data, onSaved }) {
  const [mode, setMode] = useState("links");
  const [id, setId] = useState("");
  const [clutter, setClutter] = useState(0);
  const [freq, setFreq] = useState("");
  const [cu, setCu] = useState({ a: "", b: "", ha: 20, hb: 20 });
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const site = (sid) => data.sites.find((s) => s.id === sid);
  const run = async () => {
    setBusy(true); setRes(null);
    try {
      let r;
      if (mode === "custom") {
        const A = site(cu.a), B = site(cu.b);
        if (!A?.lat || !B?.lat) throw new Error("Wybierz dwie bazy z lokalizacją");
        r = await api.post("/wifi/plan/profile", { a: { lat: A.lat, lng: A.lng, height_m: cu.ha }, b: { lat: B.lat, lng: B.lng, height_m: cu.hb }, freq_mhz: freq || 5500, clutter_m: clutter });
      } else {
        if (!id) throw new Error("Wybierz obiekt");
        r = await api.post(`/wifi/plan/analyze/${mode}/${id}`, { clutter_m: clutter, freq_mhz: freq || undefined });
        onSaved();
      }
      setRes(r.data);
    } catch (e) { toast.error(e.response ? errMsg(e) : e.message); }
    setBusy(false);
  };
  const list = mode === "links" ? data.links : data.installations;
  return (
    <Box title="Analiza LOS i strefy Fresnela (profil terenu)" tid="wifi-los">
      <div className="flex flex-wrap gap-2 items-end">
        <select value={mode} onChange={(e) => { setMode(e.target.value); setId(""); setRes(null); }} className={inp} data-testid="wifi-los-mode">
          <option value="links">Most PTP</option><option value="installations">Abonent radiowy (baza → CPE)</option><option value="custom">Projekt: dwie bazy</option>
        </select>
        {mode !== "custom" ? (
          <select value={id} onChange={(e) => setId(e.target.value)} className={`${inp} min-w-[200px]`} data-testid="wifi-los-object">
            <option value="">— wybierz —</option>
            {list.map((o) => <option key={o.id} value={o.id}>{o.name || o.client_name}{o.los ? ` · ${o.los.worst_ratio_pct}% F1` : ""}</option>)}
          </select>
        ) : (
          <>
            {["a", "b"].map((k) => (
              <span key={k} className="flex gap-1 items-center">{k.toUpperCase()}
                <select value={cu[k]} onChange={(e) => setCu({ ...cu, [k]: e.target.value })} className={inp} data-testid={`wifi-los-site-${k}`}>
                  <option value="">— baza —</option>{data.sites.filter((s) => s.lat != null).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <input type="number" value={cu[`h${k}`]} onChange={(e) => setCu({ ...cu, [`h${k}`]: e.target.value })} className={`${inp} w-14`} title="Wysokość anteny [m]" data-testid={`wifi-los-h-${k}`} />m
              </span>
            ))}
          </>
        )}
        <label className="flex flex-col text-slate-400">MHz<input type="number" value={freq} placeholder="z obiektu" onChange={(e) => setFreq(e.target.value)} className={`${inp} w-24`} data-testid="wifi-los-freq" /></label>
        <label className="flex flex-col text-slate-400">Przeszkody [m]<input type="number" value={clutter} onChange={(e) => setClutter(e.target.value)} className={`${inp} w-20`} data-testid="wifi-los-clutter" /></label>
        <button onClick={run} disabled={busy} className="bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 rounded px-3 py-1" data-testid="wifi-los-run">{busy ? "Pobieram teren…" : "Analizuj"}</button>
      </div>
      {res && <Result p={res} />}
    </Box>
  );
}

function Conflicts({ reloadKey }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/wifi/plan/conflicts").then((r) => setRows(r.data)).catch(() => setRows([])); }, [reloadKey]);
  return (
    <Box title={`Konflikty częstotliwości${rows ? ` (${rows.length})` : ""}`} tid="wifi-conflicts">
      {rows?.length === 0 && <div className="text-emerald-300">Brak nakładających się kanałów w zasięgu.</div>}
      {(rows || []).map((c, i) => (
        <div key={i} className={`border rounded px-2 py-1 mb-1 ${LVL[c.level]}`} data-testid={`wifi-conflict-${i}`}>
          <b>{c.msg}</b> · {c.a.name} ({c.a.f} MHz/{c.a.w}) ↔ {c.b.name} ({c.b.f} MHz/{c.b.w}){c.distance_m ? ` · ${(c.distance_m / 1000).toFixed(2)} km` : ""}
        </div>
      ))}
    </Box>
  );
}

function Channels({ sites }) {
  const [sid, setSid] = useState("");
  const [width, setWidth] = useState(20);
  const [radius, setRadius] = useState(5000);
  const [res, setRes] = useState(null);
  const run = () => api.get(`/wifi/plan/channels?site_id=${sid}&width_mhz=${width}&radius_m=${radius}`).then((r) => setRes(r.data)).catch((e) => toast.error(errMsg(e)));
  return (
    <Box title="Dobór wolnego kanału (pasma bez pozwolenia PL)" tid="wifi-channels">
      <div className="flex flex-wrap gap-2 items-end">
        <select value={sid} onChange={(e) => setSid(e.target.value)} className={inp} data-testid="wifi-ch-site"><option value="">— baza —</option>{sites.filter((s) => s.lat != null).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>
        <select value={width} onChange={(e) => setWidth(e.target.value)} className={inp} data-testid="wifi-ch-width">{[20, 40, 80].map((w) => <option key={w} value={w}>{w} MHz</option>)}</select>
        <label className="flex flex-col text-slate-400">Promień [m]<input type="number" value={radius} onChange={(e) => setRadius(e.target.value)} className={`${inp} w-24`} data-testid="wifi-ch-radius" /></label>
        <button onClick={run} disabled={!sid} className="bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 rounded px-3 py-1" data-testid="wifi-ch-run">Sprawdź</button>
      </div>
      {res && (
        <div className="mt-2 space-y-0.5" data-testid="wifi-ch-result">
          <div className="text-slate-400">Nadajniki w okolicy: {res.emitters_nearby}</div>
          {res.channels.slice(0, 12).map((c) => (
            <div key={c.freq_mhz} className={`font-mono ${c.conflicts ? "text-red-300" : c.adjacent ? "text-amber-300" : "text-emerald-300"}`}>
              {c.freq_mhz} MHz · kan. {c.channel} · {c.width_mhz} MHz · EIRP ≤ {c.eirp_max} dBm · {c.conflicts ? `zajęty: ${c.used_by.join(", ")}` : c.adjacent ? "wolny (sąsiedni zajęty)" : "wolny"}
            </div>
          ))}
          <div className="text-[10px] text-slate-500">5470–5725 MHz i 5725–5875 MHz wymagają DFS/TPC.</div>
        </div>
      )}
    </Box>
  );
}

const KIND_L = { sites: "Baza", devices: "Urządzenie", links: "Most PTP", installations: "Abonent" };
function Projects({ reloadKey, onChanged, canEdit }) {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/wifi/plan/projects").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, [reloadKey]);
  const act = async (r, force = false) => {
    try { await api.post(`/wifi/plan/activate/${r.kind}/${r.id}`, { force }); toast.success(`Uruchomiono: ${r.name}`); load(); onChanged(); }
    catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.issues && window.confirm(`${d.msg}:\n- ${d.issues.join("\n- ")}\n\nUruchomić mimo to?`)) return act(r, true);
      if (e.response?.status !== 409) toast.error(errMsg(e));
    }
  };
  return (
    <Box title={`Projekt → aktywna infrastruktura (planowane: ${rows.length})`} tid="wifi-projects">
      {rows.length === 0 && <div className="text-slate-500">Brak obiektów o statusie „planowany”.</div>}
      {rows.map((r) => (
        <div key={r.id} className="flex items-center gap-2 py-0.5 border-b border-slate-800" data-testid={`wifi-project-${r.id}`}>
          <span className="text-slate-500 w-24">{KIND_L[r.kind]}</span><span className="flex-1 truncate">{r.name}{r.category ? ` · ${r.category}` : ""}</span>
          {r.los && <span className={LVL[r.los.level].split(" ")[0]}>{r.los.worst_ratio_pct}% F1</span>}
          {canEdit && <button onClick={() => act(r)} className="bg-emerald-700 hover:bg-emerald-600 rounded px-2 py-0.5" data-testid={`wifi-activate-${r.id}`}>Uruchom</button>}
        </div>
      ))}
      <div className="text-[10px] text-slate-500 mt-1">Przed uruchomieniem system sprawdza limity UKE/ETSI, konflikty kanałów i wynik analizy terenu.</div>
    </Box>
  );
}

export default function PlanningPanel({ data, onChanged, canEdit }) {
  const [k, setK] = useState(0);
  const changed = () => { setK((x) => x + 1); onChanged(); };
  return (
    <div className="flex-1 overflow-y-auto" data-testid="wifi-planning">
      <LosAnalyzer data={data} onSaved={changed} />
      <Conflicts reloadKey={k} />
      <Channels sites={data.sites} />
      <Projects reloadKey={k} onChanged={changed} canEdit={canEdit} />
    </div>
  );
}
